import { useState, useEffect } from "react";
import { X, BookOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API_URL = import.meta.env.VITE_API_URL || "http://64.23.240.10/api/v1";
const BASE_API_URL = API_URL.replace("/v1", "");

export default function ExitIntentPanel() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (dismissed || submitted) return;

    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    function handleMouseLeave(e: MouseEvent) {
      if (e.clientY <= 5 && !show && !dismissed && !submitted) {
        setShow(true);
      }
    }
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [show, dismissed, submitted]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setSubmitting(true);
    try {
      await fetch(`${BASE_API_URL}/store/lead-capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, manual_interest: "manual2", source: "exit_intent" }),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  }

  function dismiss() {
    setShow(false);
    setDismissed(true);
  }

  return (
    <AnimatePresence>
      {show && !dismissed && (
        <motion.div
          initial={{ x: 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 400, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 z-40 w-96 bg-navy-dark border border-sky-blue/20 rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="p-6">
            <button
              onClick={dismiss}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-sky-blue/20 flex items-center justify-center">
                <BookOpen size={20} className="text-sky-blue" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Before you go — Chapter 1 is free.</p>
              </div>
            </div>

            {!submitted ? (
              <>
                <p className="text-white/60 text-xs mb-4 leading-relaxed">
                  Enter your email to get the first chapter of The Crypto Staking Intelligence Guide immediately. No purchase required.
                </p>
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-steel-blue/10 border border-sky-blue/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30"
                    required
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-sky-blue hover:bg-sky-blue/90 disabled:opacity-40 text-navy-dark font-bold rounded-lg transition text-sm whitespace-nowrap"
                  >
                    {submitting ? "..." : "Send"}
                  </button>
                </form>
              </>
            ) : (
              <p className="text-sky-blue text-sm">Check your email — Chapter 1 is on its way.</p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
